import { Callable, TimeoutId, Unpromise } from '@prophecy/interfaces/generic';
import { debounce, DebouncedFunc, DebounceSettings, isEqual, isNil, noop, throttle, ThrottleSettings } from 'lodash-es';
import { nanoid } from 'nanoid';
import { DependencyList, EffectCallback, Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { DraftFunction, useImmer } from 'use-immer';

import { isNodeEvnProduction } from '../env';
import { isDependencySame } from '../memoize';

/**
 * a hook which keeps state in a ref so the ref
 * can be passed around and it will always point to latest value.
 *
 * Why not useRef: useRef doesn't cause re render, which we need in case of state change
 */
export function useStateRef<T>(initialState: T | (() => T)) {
  const [, setState, stateRef] = useReactiveState(initialState);
  return [stateRef, setState] as const;
}

/**
 * Hook for updating states in render phase, if prop changes
 * Note: this doesn't return a state like getDerivedStateFromProps, but instead, you can set your state inside
 * this hook. This is just useMemo without any return value, added only for semantic
 *
 * Use this hook very sparingly only when your state is dependent to props
 * */
export function useDeriveStateFromProps(cb: () => void, dependencies: unknown[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(cb, dependencies);
}

/**
 * Hook to simulate controlled/uncontrolled state of input elements for other components.
 */
export function useControlledState<T>({
  value,
  defaultValue,
  onChange = noop
}: {
  value?: T;
  defaultValue?: T;
  onChange?: (value: T, ...args: unknown[]) => void;
}): [T | undefined, (value: T) => void] {
  const [internalState, setInternalState] = useState(defaultValue);
  const isControlled = !(value === undefined || value === null);
  const state = isControlled ? value : internalState;

  const setState = usePersistentCallback((state: T, ...args: unknown[]) => {
    // set internal state if component is uncontrolled, or with state update we are making it uncontrolled
    // which would be when we change value to undefined.
    if (!isControlled || state === undefined) setInternalState(state);

    onChange(state, ...args);
  });

  return [state, setState];
}

export function usePersistentCallback<T extends Callable>(cb: T) {
  const callbackRef = useRef<T>(cb);
  // keep the callback ref upto date
  callbackRef.current = cb;
  type Params = Parameters<T>;
  /**
   * initialize a persistent callback which never changes
   * through out the component lifecycle
   */
  const persistentCbRef = useRef(function (...args: Params) {
    return callbackRef.current(...args);
  } as T);

  return persistentCbRef.current;
}

export function useMemoizedDependencies(dependencies: React.DependencyList) {
  const dependencyAry = useRef<React.DependencyList>(dependencies);

  // if dependency array has changed update the current value of it
  if (!isEqual(dependencies, dependencyAry.current)) {
    dependencyAry.current = dependencies;
  }

  return dependencyAry.current;
}

/**
 * useEffect but deeply compares the dependency array, useful when the reference changes frequently
 * but the underlying data doesn't update
 */
export function useDeepCompareEffect(cb: React.EffectCallback, dependencies: React.DependencyList) {
  const dependencyAry = useMemoizedDependencies(dependencies);

  // eslint not able to non inline callbacks
  // eslint-disable-next-line react-hooks/exhaustive-deps,
  useEffect(cb, dependencyAry);
}

export enum WatchStrategy {
  Immediate = 'Immediate',
  AfterRender = 'AfterRender'
}

/**
 * hook to watch on dependent values and run the callback.
 * It supports two strategies Immediate and AfterRender
 * Immediate: runs the callback immediately when dependencies change in same render cycle
 * AfterRender: runs the callback after the changes are committed
 */
export function useWatch<T extends React.DependencyList>(
  callback: (prevDependencies: T, currentDependencies: T) => void,
  dependencies: T,
  strategy: WatchStrategy = WatchStrategy.Immediate
) {
  const persistentCallback = usePersistentCallback(callback);
  const lastStrategy = useRef<WatchStrategy>(strategy);
  if (lastStrategy.current !== strategy) {
    throw new Error('Watch strategy cannot be changed');
  }

  const lastDependencies = useRef<T>(dependencies);
  const hook = strategy === WatchStrategy.Immediate ? useMemo : useEffect;

  hook(() => {
    const _lastDependencies = lastDependencies.current;
    lastDependencies.current = dependencies;
    persistentCallback(_lastDependencies, dependencies);
  }, dependencies);
}

/**
 * useMemo but deeply compares the dependency array, useful when the reference changes frequently
 * but the underlying data doesn't update
 */
export function useDeepCompareMemo<T = unknown>(cb: () => T, dependencies: React.DependencyList) {
  const dependencyAry = useMemoizedDependencies(dependencies);

  // eslint not able to non inline callbacks
  // eslint-disable-next-line react-hooks/exhaustive-deps,
  return useMemo(cb, dependencyAry);
}

export function useThrottle<T extends Callable>(cb: T, delay?: number, options?: ThrottleSettings) {
  const persistentCallback = usePersistentCallback(cb);
  const throttledFunc = useRef<ReturnType<typeof throttle>>();
  type Params = Parameters<T>;
  return useMemo(() => {
    let lastThrottledFunc = throttledFunc.current;

    throttledFunc.current = throttle(
      (...args: Params) => {
        // if there is existing throttledFunc cancel it
        if (lastThrottledFunc) {
          lastThrottledFunc.cancel();
          lastThrottledFunc = undefined;
        }

        persistentCallback(...args);
      },
      delay,
      options
    );

    return throttledFunc.current!;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistentCallback, delay, JSON.stringify(options)]);
}

export function useDebounce<T extends Callable>(cb: T, delay?: number, options?: DebounceSettings) {
  const persistentCallback = usePersistentCallback(cb);
  const debouncedFunc = useRef<DebouncedFunc<T>>();
  type Params = Parameters<T>;
  return useMemo(() => {
    let lastDebouncedFunc = debouncedFunc.current;

    debouncedFunc.current = debounce(
      (...args: Params) => {
        // if there is existing debouncedFunc cancel it
        if (lastDebouncedFunc) {
          lastDebouncedFunc.cancel();
          lastDebouncedFunc = undefined;
        }

        persistentCallback(...args);
      },
      delay,
      options
    );

    return debouncedFunc.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistentCallback, delay, JSON.stringify(options)]);
}

// a hook to batch the calls for functions and send all the payload at once
export function useBatchedFunction<P, T extends Callable>(cb: T, delay: number = 1000, options?: ThrottleSettings) {
  const payloads = useRef<P[][]>([]);

  const callBatchedPayload = () => {
    cb(...payloads.current);
    payloads.current = [];
  };

  const throttleFn = useThrottle(callBatchedPayload, delay, options);

  const batchedFn = usePersistentCallback((...payload: P[]) => {
    payloads.current.push(payload);
    throttleFn();
  }) as typeof throttleFn;

  batchedFn.cancel = throttleFn.cancel;
  batchedFn.flush = throttleFn.flush;

  return batchedFn;
}

function useTimedFlagCore(timeout: number, setFlag: (flag: boolean) => void) {
  const timeoutRef = useRef<TimeoutId>();

  const _setFlag = usePersistentCallback((_flag: boolean = true) => {
    setFlag(_flag);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // set the reset flag only if flag is true
    if (_flag) {
      timeoutRef.current = setTimeout(() => {
        setFlag(false);
      }, timeout);
    }
  });

  return _setFlag;
}

export function useTimedFlag(timeout: number = 500): [boolean, (flag?: boolean) => void] {
  const [flag, setFlag] = useState<boolean>(false);

  const _setFlag = useTimedFlagCore(timeout, setFlag);

  return [flag, _setFlag];
}

export function useTimedFlagRef(timeout: number = 500): [React.MutableRefObject<boolean>, (flag?: boolean) => void] {
  const flagRef = useRef<boolean>(false);

  const _setFlag = useTimedFlagCore(timeout, (flag) => (flagRef.current = flag));

  return [flagRef, _setFlag];
}

export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  savedCallback.current = callback;

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    return () => clearInterval(id);
  }, [delay]);
}

/*
 * this hook will tell whenever a dependancy is changed in the same render
 * in the render when dependency changed this will return true and it will return false in next render
 */
export const useDidChange = (deps: Array<unknown>, equalityFunction?: (a: unknown, b: unknown) => boolean) => {
  const lastDependencyRef = useRef<Array<unknown>>();

  // get the last dependency
  const lastDependency = lastDependencyRef.current;

  //set the new deps as last dependency
  lastDependencyRef.current = deps;

  // for the first time mark deps as changed, and the check with last deps
  return !lastDependency || !isDependencySame(deps, lastDependency, equalityFunction);
};

/**
 * useLazyEffect runs the callback every time after the initial render.
 * If a lazyCondition is provided, the initial render is considered to be the first time lazyCondition turns true.
 * After that, the callback runs on every deps change.
 */
export const useLazyEffect = (callback: Callable, deps: Array<unknown>, lazyCondition: () => boolean = () => true) => {
  const hasActivated = useRef(false);
  const persistentLazyCondition = usePersistentCallback(lazyCondition);
  const persistentCallback = usePersistentCallback(callback);

  useEffect(() => {
    if (!hasActivated.current) {
      if (persistentLazyCondition()) {
        hasActivated.current = true;
        // Do not run callback on this activation, only mark as activated
      }
      // If not activated, do nothing
      return;
    }
    // After activation, run callback on every deps change
    return persistentCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

/**
 * Executes the callback once when the condition turns true, and never again.
 */
export const useOnceOn = <T>(callback: () => T, deps: Array<unknown>, condition: () => boolean) => {
  const hasRun = useRef(false);
  const persistentCallback = usePersistentCallback(callback);
  const persistentCondition = usePersistentCallback(condition);
  const [output, setOutput] = useState<T>();

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hasRun.current && persistentCondition()) {
      hasRun.current = true;
      setOutput(persistentCallback());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return output;
};

/**
 * Logs a warning in development mode when a component unexpectedly remounts.
 * Useful for catching unintentional remounts due to key changes, incorrect memoization, or state resetting.
 */
export const useRemountCheck = (() => {
  const mountedComponents = new Set<string | undefined>();
  return (name: string) => {
    if (useDidChange([])) {
      if (mountedComponents.has(name)) {
        if (!isNodeEvnProduction()) {
          console.warn(`Component remounted: ${name}`);
        }
      } else {
        mountedComponents.add(name);
      }
    }
  };
})();

/**
 * React's useEffect are not guranteed to be async, but sometime we
 * need to do after previous render and paint is done, this hook can be used there
 * note: with this hook we can't have cleanup function
 */
export function useAsyncEffect(effect: EffectCallback, dependencies?: DependencyList) {
  return useEffect(() => {
    requestAnimationFrame(() => {
      effect();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
}

// this hook debounces the value change and keep returning the old value while debounced
export function useDebouncedValue<T>(value: T, delay: number = 500) {
  const [debouncedValue, setValue, ...rest] = useReactiveState(value);
  const debouncedSetValue = useDebounce(setValue, delay);
  useEffect(() => {
    debouncedSetValue(value);
  }, [debouncedSetValue, value]);
  useEffect(() => () => debouncedSetValue.cancel(), [debouncedSetValue]);
  return [debouncedValue, debouncedSetValue, ...rest] as const;
}

export function useBatchedOnAnimationFrame<T extends Callable>(callback: T): DebouncedFunc<T> {
  const raf = useRef<number>();
  const callRefs = useRef<Array<() => void>>([]);

  const rafDebouncedCallback = usePersistentCallback((...args: Parameters<T>) => {
    callRefs.current.push(() => callback(...args));

    if (!raf.current) {
      raf.current = requestAnimationFrame(() => {
        //TODO: remove manual batching once react 18 is added, as react 18 automatically does it
        ReactDOM.unstable_batchedUpdates(() => {
          callRefs.current.forEach((cb) => cb());
        });
        callRefs.current = [];
        raf.current = undefined;
      });
    }
  }) as DebouncedFunc<T>;

  rafDebouncedCallback.cancel = () => {
    if (raf.current) {
      cancelAnimationFrame(raf.current);
      callRefs.current = [];
    }
  };

  rafDebouncedCallback.flush = () => {
    callRefs.current.forEach((cb) => cb());
    rafDebouncedCallback.cancel();
    return undefined;
  };

  return rafDebouncedCallback;
}

export const useLatestState = <T>(value: T) => {
  const [state, setState, ...rest] = useReactiveState(value);

  useMemo(() => {
    setState(value);
  }, [setState, value]);

  return [state, setState, ...rest] as const;
};

export const useDependantState = <T, S extends Array<unknown>>(getter: (...args: S) => T, deps: S) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const externalState = useMemo(() => getter(...deps), deps);
  return useLatestState(externalState);
};

/**
 * A hook that returns a ref object whose `.current` value always reflects the latest state.
 *
 * This is useful for accessing the latest value inside asynchronous callbacks,
 * event handlers, or other memoized functions without triggering re-renders.
 */
export function useLatestRef<T>(state: T): React.MutableRefObject<T> {
  const stateRef = useRef(state);
  stateRef.current = state;
  return stateRef;
}

export const useOnce = <T>(callback: () => T) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(callback, []);
};

// a flag which tells if an app is mounted. Useful for cases where some async func is running and responds after long
export function useIsMounted() {
  // during first render cycle as well, keep the value as mounted.
  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  return mounted;
}

// this flag check if react update is pending as result of setState
export function useIsReactUpdatePending<C extends Function>(setState: C) {
  const reactUpdatePending = useRef<boolean>();

  useEffect(() => {
    reactUpdatePending.current = false;
  });

  const _setState = usePersistentCallback((...args) => {
    reactUpdatePending.current = true;
    return setState(...args);
  }) as unknown as C;

  return [reactUpdatePending.current, _setState] as [boolean, C];
}

export function useWindowEvent<EventHandlerType extends Callable>(
  event: string,
  handler: EventHandlerType,
  captureMode?: boolean
) {
  const persistentHandler = usePersistentCallback(handler);

  useEffect(() => {
    window.addEventListener(event, persistentHandler, captureMode);

    return () => {
      window.removeEventListener(event, persistentHandler, captureMode);
    };
  }, [captureMode, event, persistentHandler]);
}

export function useOnMount(cb: Callable) {
  const _cb = usePersistentCallback(cb);
  useEffect(_cb, [_cb]);
}

export function useOnUnmount(cb: Callable) {
  const _cb = usePersistentCallback(cb);
  useEffect(() => {
    return () => {
      _cb();
    };
  }, [_cb]);
}

// Click away listener
export const useClickAwayListener = <T extends HTMLElement = HTMLElement>(
  handler: Callable,
  ignore?: (element: HTMLElement | null) => boolean,
  options?: boolean | AddEventListenerOptions,
  eventType?: 'pointerdown' | 'click'
) => {
  const ref = useRef<T | null>(null);
  const persistentHandler = usePersistentCallback(handler);
  const persistentIgnore = usePersistentCallback(ignore || (() => false));

  useEffect(() => {
    const handleClick = (event: React.MouseEvent | React.KeyboardEvent | React.SyntheticEvent | Event) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node | null) &&
        !persistentIgnore(event.target as HTMLElement | null)
      ) {
        persistentHandler(event);
      }
    };

    document.addEventListener(eventType || 'click', handleClick, options);
    return () => {
      document.removeEventListener(eventType || 'click', handleClick, options);
    };
  }, [ref, eventType, persistentHandler, persistentIgnore, options]);

  return ref;
};

//use this very sparingly or else, it will trigger multiple rerenders instead of one
export const useImmerWithCallback = <T>(
  initialStateValue: T
): [state: T, setState: (updatedState: T | DraftFunction<T>, callback?: (updatedState: T) => void) => void] => {
  const [state, setCurrentState] = useImmer<T>(initialStateValue);
  const immerCallbackRefList = useRef<((updated: T) => void)[]>([]);

  const setState = useCallback(
    (updatedState: T | DraftFunction<T>, callback?: (updatedState: T) => void) => {
      if (callback) {
        immerCallbackRefList.current = [...immerCallbackRefList.current, callback];
      }
      setCurrentState(updatedState);
    },
    [immerCallbackRefList, setCurrentState]
  );

  useEffect(() => {
    const callbackList = immerCallbackRefList.current;
    if (callbackList.length > 0) {
      immerCallbackRefList.current = [];
      callbackList.forEach((callback) => {
        if (typeof callback === 'function') {
          callback(state);
        }
      });
    }
  }, [state]);
  return [state, setState];
};

export function useThenable<R = unknown>(thenable: Promise<R>) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [value, setValue] = useState<R>();

  useEffect(() => {
    setIsLoading(true);
    thenable
      .then((value) => setValue(value))
      .catch((err) => setError(err))
      .finally(() => setIsLoading(false));
  }, [error, thenable]);

  return { isLoading, value, error };
}

// throttle the function calls and execute once per requestAnimationFrame
export const useFrameThrottle = <T extends Callable>(cb: T, trailing?: boolean) => {
  const inQueueRef = useRef<Parameters<T> | null | undefined>(undefined);

  const persistentCallback = usePersistentCallback((isTrailing?: boolean, ...args: Parameters<T>) => {
    if (!isTrailing) cb(...args);
    requestAnimationFrame(() => {
      // if another callback came in while waiting execute it
      if (!isNil(inQueueRef.current)) {
        persistentCallback(false, ...inQueueRef.current);
      }
      inQueueRef.current = undefined;
    });
  });

  return usePersistentCallback((...args: Parameters<T>) => {
    // if waiting for next tick, update the args
    if (inQueueRef.current !== undefined) {
      inQueueRef.current = args;
      return;
    }

    // if trailing, store args and start tick, else execute immediately and start tick
    inQueueRef.current = trailing ? args : null;
    persistentCallback(trailing, ...args);
  });
};

export const useForceUpdate = () => {
  const [, setToggle] = useState(false);
  return usePersistentCallback(() => setToggle((toggle) => !toggle));
};

export const useConsole = (...deps: unknown[]) => {
  if (useDidChange(deps)) {
    console.log(...deps);
  }
};

/**
 * Function to call a callback after one render cycle of react.
 * You can increase the cycle by nesting the afterNextRenderCalls
 */
export function useAfterNextRender() {
  const [counter, setCounter] = useState(0);
  const callbackRef = useRef<Callable[]>();
  const isMounted = useIsMounted();

  useEffect(() => {
    if (callbackRef.current) {
      const cbs = callbackRef.current;
      callbackRef.current = undefined;
      cbs.forEach((cb) => cb());
    }
  }, [counter]);

  const afterNextRender = usePersistentCallback((callback: Callable) => {
    if (!isMounted.current) return;
    callbackRef.current = callbackRef.current ? [...callbackRef.current, callback] : [callback];
    setCounter((c) => c + 1);
  });

  const awaitNextRender = usePersistentCallback(async () => {
    return new Promise((resolve) => {
      afterNextRender(() => {
        resolve(true);
      });
    });
  });

  return { afterNextRender, awaitNextRender };
}

export const useConditionalHook = <T>(callbackWithHook: () => T, propCondition: boolean, elseValue: T) => {
  // useOnce is triggered once per component, so the condition never changes
  if (useOnce(() => propCondition)) {
    return callbackWithHook();
  }
  return elseValue;
};

export const useIsFirstRender = () => {
  const isFirstRender = useRef(true);
  useEffect(() => {
    isFirstRender.current = false;
  }, []);
  return isFirstRender.current;
};

export const useUUID = () => {
  const keyRef = useRef<Key>(nanoid());

  const resetKey = usePersistentCallback(() => {
    keyRef.current = nanoid();
  });

  return [keyRef.current, resetKey] as const;
};

export const useReactiveUUID = () => {
  const [key, setKey] = useState<Key>(nanoid());

  const resetKey = usePersistentCallback(() => {
    setKey(nanoid());
  });

  return [key, resetKey] as const;
};

export const getNextState = <T>(prevState: T, nextState: React.SetStateAction<T>) => {
  return typeof nextState === 'function' ? (nextState as Callable)(prevState) : nextState;
};

/**
 * An enhanced `useState` hook that exposes the latest state via a ref and allows
 * optional post-update callbacks.
 */
export const useReactiveState = <T>(initialState: T | (() => T)) => {
  const [state, setState] = useState(initialState);
  const currentStateRef = useLatestRef(state);

  return [
    state,
    usePersistentCallback((nextState: React.SetStateAction<T>, callback?: (newState: T) => unknown) => {
      currentStateRef.current = getNextState(currentStateRef.current, nextState);
      setState(currentStateRef.current);
      if (callback && typeof callback === 'function') callback(currentStateRef.current);
    }),
    currentStateRef
  ] as const;
};

// doesn't work well when execution is in progress and
// cancel prompt is shown and redirection is prevented via useIDEPrompt
export const useSearchParamState = <T = string>(
  key: string,
  options: {
    initialValue?: T;
    serialize?: (value: T) => string;
    deserialize?: (value: string) => T;
  } = {}
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const serialize = options.serialize || ((value) => String(value));
  const deserialize = options.deserialize || ((value) => value as T);

  const setState = usePersistentCallback((value?: T) => {
    if (isNil(value)) searchParams.delete(key);
    else searchParams.set(key, serialize(value));
    setSearchParams(searchParams);
  });

  const value = searchParams.get(key);
  const deserializedValue = !isNil(value) ? deserialize(value) : options.initialValue;

  return [deserializedValue, setState] as const;
};

export const useLoadableCallback = <T extends (...args: never[]) => Promise<unknown>>(callback: T) => {
  const [isLoading, setIsLoading] = useState(false);
  const persistantCallback = usePersistentCallback(callback);

  return [
    isLoading,
    usePersistentCallback(async (...args: never[]) => {
      setIsLoading(true);
      let response: Unpromise<ReturnType<T>>;
      try {
        response = (await persistantCallback(...args)) as Unpromise<ReturnType<T>>;
      } finally {
        setIsLoading(false);
      }
      return response;
    }) as T
  ] as const;
};

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
